import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../../i18n/i18n';
import i18n from '../../../i18n/i18n';
import {
  IAdminNewsFeedListResponse,
  IAdminNewsFeedState,
  IAdminNewsFeedDeletePostResponse,
} from '../../../interface/classveew-admin/news-feed.interface';
import {
  fetchAdminNewsFeedPosts,
  fetchPostComments,
  deleteAdminPost,
  deleteAdminComment,
} from '../../action/classveew-admin/news-feed.action';

const DEFAULT_PAGE_SIZE = 20;

const initialState: IAdminNewsFeedState = {
  posts: [],
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  total: 0,
  totalPages: 0,
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  error: null,

  // Comments (paginated)
  comments: [],
  commentsPage: 0,
  commentsTotalPages: 0,
  commentsTotal: 0,
  commentsHasMore: true,
  selectedPostId: null,
  isLoadingComments: false,
  isLoadingMoreComments: false,
  commentsError: null,

  // Deleting state
  isDeletingPost: false,
  isDeletingComment: false,
};

const adminNewsFeedSlice = createSlice({
  name: 'adminNewsFeed',
  initialState,
  reducers: {
    /** Reset feed to initial state (e.g. on filter/search change) */
    resetNewsFeed: () => initialState,

    /** Clear only the error */
    clearNewsFeedError: (state) => {
      state.error = null;
    },

    /** Set the selected post for inline detail/comments view */
    setSelectedPostId: (state, action: PayloadAction<string | null>) => {
      state.selectedPostId = action.payload;
      if (!action.payload) {
        // Reset all comments state when deselecting
        state.comments = [];
        state.commentsPage = 0;
        state.commentsTotalPages = 0;
        state.commentsTotal = 0;
        state.commentsHasMore = true;
        state.commentsError = null;
        state.isLoadingComments = false;
        state.isLoadingMoreComments = false;
      }
    },

    /** Clear comments state */
    clearComments: (state) => {
      state.comments = [];
      state.commentsPage = 0;
      state.commentsTotalPages = 0;
      state.commentsTotal = 0;
      state.commentsHasMore = true;
      state.commentsError = null;
      state.isLoadingComments = false;
      state.isLoadingMoreComments = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Pending ──────────────────────────────────
      .addCase(fetchAdminNewsFeedPosts.pending, (state, action) => {
        const requestedPage = action.meta.arg.page;
        if (requestedPage === 1) {
          // Fresh load or filter change
          state.isLoading = true;
          state.isLoadingMore = false;
          state.posts = [];
        } else {
          // Infinite scroll – loading more
          state.isLoadingMore = true;
        }
        state.error = null;
      })

      // ── Fulfilled ────────────────────────────────
      .addCase(
        fetchAdminNewsFeedPosts.fulfilled,
        (state, action: PayloadAction<IAdminNewsFeedListResponse>) => {
          const { posts, page, pageSize, total, totalPages } = action.payload.data;

          if (page === 1) {
            state.posts = posts;
          } else {
            // Append new posts for infinite scroll (deduplicate by id)
            const existingIds = new Set(state.posts.map((p) => p.id));
            const newPosts = posts.filter((p) => !existingIds.has(p.id));
            state.posts = [...state.posts, ...newPosts];
          }

          state.page = page;
          state.pageSize = pageSize;
          state.total = total;
          state.totalPages = totalPages;
          state.hasMore = page < totalPages;
          state.isLoading = false;
          state.isLoadingMore = false;
          state.error = null;
        },
      )

      // ── Rejected ─────────────────────────────────
      .addCase(fetchAdminNewsFeedPosts.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_news_feed_posts', 'Failed to fetch news feed posts.');
      })

      // ── Comments: Pending ────────────────────────
      .addCase(fetchPostComments.pending, (state, action) => {
        const requestedPage = action.meta.arg.page;
        if (requestedPage === 1) {
          state.isLoadingComments = true;
          state.isLoadingMoreComments = false;
          state.comments = [];
        } else {
          state.isLoadingMoreComments = true;
        }
        state.commentsError = null;
      })

      // ── Comments: Fulfilled ──────────────────────
      .addCase(fetchPostComments.fulfilled, (state, action) => {
        // Prevent race condition: ignore stale responses from previously viewed posts
        const requestedPostId = action.meta.arg.postId;
        if (requestedPostId !== state.selectedPostId) {
          // Stale request from a previous post — discard the response
          state.isLoadingComments = false;
          state.isLoadingMoreComments = false;
          return;
        }

        const { comments, page, totalPages, total } = action.payload.data;

        if (page === 1) {
          state.comments = comments;
        } else {
          // Append (deduplicate by id)
          const existingIds = new Set(state.comments.map((c) => c.id));
          const newComments = comments.filter((c) => !existingIds.has(c.id));
          state.comments = [...state.comments, ...newComments];
        }

        state.commentsPage = page;
        state.commentsTotalPages = totalPages;
        state.commentsTotal = total;
        state.commentsHasMore = page < totalPages;
        state.isLoadingComments = false;
        state.isLoadingMoreComments = false;
        state.commentsError = null;
      })

      // ── Comments: Rejected ───────────────────────
      .addCase(fetchPostComments.rejected, (state, action) => {
        state.isLoadingComments = false;
        state.isLoadingMoreComments = false;
        state.commentsError =
          action.payload?.message || tr('g.failed_to_fetch_comments', 'Failed to fetch comments.');
      })

      // ── Delete Post: Pending ─────────────────────
      .addCase(deleteAdminPost.pending, (state) => {
        state.isDeletingPost = true;
      })

      // ── Delete Post: Fulfilled ───────────────────
      .addCase(
        deleteAdminPost.fulfilled,
        (state, action: PayloadAction<IAdminNewsFeedDeletePostResponse>) => {
          const deletedPostId = action.payload.data.postId;
          state.posts = state.posts.filter((p) => p.id !== deletedPostId);
          state.total = Math.max(0, state.total - 1);
          state.isDeletingPost = false;

          // If the deleted post was the selected one, deselect it
          if (state.selectedPostId === deletedPostId) {
            state.selectedPostId = null;
            state.comments = [];
            state.commentsPage = 0;
            state.commentsTotalPages = 0;
            state.commentsTotal = 0;
            state.commentsHasMore = true;
            state.commentsError = null;
          }
        },
      )

      // ── Delete Post: Rejected ────────────────────
      .addCase(deleteAdminPost.rejected, (state) => {
        state.isDeletingPost = false;
      })

      // ── Delete Comment: Pending ──────────────────
      .addCase(deleteAdminComment.pending, (state) => {
        state.isDeletingComment = true;
      })

      // ── Delete Comment: Fulfilled ────────────────
      .addCase(deleteAdminComment.fulfilled, (state, action) => {
        const deletedCommentId = action.payload.data.commentId;
        state.comments = state.comments.filter((c) => c.id !== deletedCommentId);
        state.commentsTotal = Math.max(0, state.commentsTotal - 1);
        state.isDeletingComment = false;

        // Update the post's commentsCount in the feed list.
        // Use the postId from the thunk args so we update the correct post
        // even if the detail view was closed and `selectedPostId` is null.
        const deletedFromPostId = action.meta.arg.postId;
        if (deletedFromPostId) {
          const post = state.posts.find((p) => p.id === deletedFromPostId);
          if (post) {
            post.commentsCount = Math.max(0, post.commentsCount - 1);
          }
        }
      })

      // ── Delete Comment: Rejected ─────────────────
      .addCase(deleteAdminComment.rejected, (state) => {
        state.isDeletingComment = false;
      });
  },
});

export const { resetNewsFeed, clearNewsFeedError, setSelectedPostId, clearComments } =
  adminNewsFeedSlice.actions;

export default adminNewsFeedSlice.reducer;
